import { useMemo } from 'react';

import type { Grant } from '../api/business/types';

import { useSession } from './SessionContext';

export type Permissions = {
  isOwner: boolean;
  /** Whether the licence lets the tenant write at all (BR-04). */
  writable: boolean;
  grants: Array<Grant>;
  /** A tenant-scoped code without a company, a company-scoped code with one. */
  can: (code: string, companyId?: string) => boolean;
  /** Whether the user holds the code on at least one company — for navigation, never for a decision. */
  canAnywhere: (code: string) => boolean;
};

/**
 * What the business user may do, read from `/auth/me`. It **hides** what they cannot use; the API decides
 * (Conventions/06 §4). The owner holds everything and carries no grants.
 */
export const usePermissions = (): Permissions => {
  const { profile } = useSession();

  return useMemo<Permissions>(() => {
    const user = profile?.kind === 'business' ? profile.user : null;
    const isOwner = user?.isOwner ?? false;
    const grants = user?.grants ?? [];

    return {
      isOwner,
      writable: user?.licence.writable ?? false,
      grants,
      can: (code: string, companyId?: string) =>
        isOwner ||
        grants.some((grant) => grant.permission === code && grant.companyId === (companyId ?? null)),
      canAnywhere: (code: string) => isOwner || grants.some((grant) => grant.permission === code),
    };
  }, [profile]);
};
